import React, { SyntheticEvent, useEffect, useState } from 'react';
import {
  Button,
  Dimmer,
  Dropdown,
  Grid,
  Icon,
  Input,
  Loader,
  Popup
} from 'semantic-ui-react';
import { useSelector } from 'react-redux';
import { iRootState } from '../../../../../store';
import {
  formatAmount,
  moneyStringToNumber,
  numberToMoneyString,
  unmaskMoney
} from '../../../../../services/masks';
import { FieldLabel, theme } from '../../../../../styles';
import * as S from './styles';
import AppMaskedInput from '../../../../../components/MaskedInput';
import { get, post } from '../../../../../services/request';
import CustomDatePicker from '../../../../../components/CustomDatePicker';
import {
  dangerNotification,
  warningNotification
} from '../../../../../services/notification';
import { SegimobCoveragesProps, SegimobQuotedProps } from '../../../types/segimob';
import SaveQuotationModal from './components/SaveQuotationModal';
import { FormProps as SaveQuotationModalFormProps } from './components/SaveQuotationModal/types';
import {
  FormatCurrency,
  PushRoute,
  UnMaskCurrency,
  isBackofficeTestUser
} from '../../../../../util';
import { useHistory, useLocation } from 'react-router-dom';
import { hasPermission } from '../../../../../components/AuthorizationContainer';
import { ActionEnum } from '../../../../../enums/authz-action.enum';
import { FeatureEnum } from '../../../../../enums/authz-feature.enum';
import moment from 'moment-timezone';
import { useOccupations } from './services/useOccupations';
import { useAssistances } from './services/useAssistances';
import { isUuid } from 'uuidv4';
import { sanitizeAddressComplement } from '../../../../../utils/string.util';
import {
  InitialAddressType,
  InitialInsuredType,
  InitialTenantType
} from '../../../../iframe/fire-insurance-budget';
import { HiInformationCircle } from 'react-icons/hi';
import AssistanceTableModal from './components/AssistanceTableModal';
import { UserRolesEnum } from '../../../../../enums/user-roles.enum';


const minRange = moment().subtract(45, 'days').toDate();
const maxRange = moment().add(25, 'days').toDate();

interface SegimobBudgetFormProps {
  bondInsuranceId?: string;
  estate: any;
  insurerId: string;
  userId: string;
  percentage?: number;
  initialRentValue?: number | null;
  initialStartDate?: string | null;
  onBudgetCreated?: (budgetId: string) => void;
  isIframe?: boolean;
  initialAddress?: InitialAddressType;
  initialTenant?: InitialTenantType;
  initialInsured?: InitialInsuredType;
}

type SegimobCoverageWithAliases = SegimobCoveragesProps & {
  coverageName?: string;
  nome?: string;
  referenceCode?: number | string;
};

const normalizeCoverageName = (name?: string) =>
  name
    ?.normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .trim();

const getCoverageName = (coverage?: SegimobCoverageWithAliases) =>
  coverage?.nomeCobertura ||
  coverage?.coverageName ||
  coverage?.nome ||
  '';

const isRequiredFireCoverage = (coverage?: SegimobCoverageWithAliases) => {
  const coverageName = normalizeCoverageName(getCoverageName(coverage));
  const isFireCoverageName =
    coverageName === 'INCENDIO' || coverageName?.startsWith('INCENDIO');

  return (
    Number(coverage?.id ?? coverage?.referenceCode) === 0 ||
    isFireCoverageName
  );
};

const sortRequiredFireCoverageFirst = (coverages: SegimobCoveragesProps[]) => {
  const requiredFireCoverage = coverages.find(coverage =>
    isRequiredFireCoverage(coverage)
  );

  if (!requiredFireCoverage) {
    return coverages;
  }

  return [
    requiredFireCoverage,
    ...coverages.filter(coverage => coverage !== requiredFireCoverage)
  ];
};

const SegimobBudgetForm = (props: SegimobBudgetFormProps) => {
  const user = useSelector((state: iRootState) => state.user);
  const isBackofficeTest = isBackofficeTestUser(user);
  const canContract =
    user.role !== UserRolesEnum.estateQuotation;
  const canViewAdvancedSettings =
    user.role !== UserRolesEnum.estateUser &&
    user.role !== UserRolesEnum.estateQuotation;
  const location = useLocation();

  const history = useHistory();
  const {
    estate,
    insurerId,
    userId,
    percentage,
    initialRentValue,
    initialStartDate,
    onBudgetCreated,
    isIframe,
    initialAddress,
    initialTenant,
    initialInsured
  } = props;
  const canEditPl =
    user.role !== UserRolesEnum.estateQuotation &&
    (user.role !== UserRolesEnum.estateUser ||
      Boolean(estate.allowEstateUserPlEdit));
  const [propertyType, setPropertyType] = useState('');
  const { occupations, loading: loadingOccupations } = useOccupations(
    estate.id,
    propertyType
  );
  const [maskedRentValue, setMaskedRentValue] = useState<string>();
  const [rentValueForComparison, setRentValueForComparison] = useState<string>();
  const [proLaboreCommissionComparison, setProLaboreCommissionComparison] = useState<
    number | undefined
  >(undefined);
  const [propertyUseType, setPropertyUseType] = useState<number>();
  const [months, setMonths] = useState<number | null>(12);
  const [startDate, setStartDate] = useState<Date | null>();
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [step, setStep] = useState(1);
  const [editableCoverageCode, setEditableCoverageCode] = useState<number>(-2);
  const [allChecked, setAllChecked] = useState<boolean>(false);
  const [editableCoverages, setEditableCoverages] = useState<SegimobCoveragesProps[]>();
  const [isHiring, setIsHiring] = useState<boolean>(false);
  const [quote, setQuote] = useState<SegimobQuotedProps>();
  const [quoteControl, setQuoteControl] = useState(false);
  const [isSaveQuotationModalOpen, setIsSaveQuotationModalOpen] =
    useState<boolean>(false);
  const [isSaveQuotationModalLoading, setIsSaveQuotationModalLoading] =
    useState<boolean>(false);
  const {
    assistances,
    loading: loadingAssistances,
    defaultAssistanceId
  } = useAssistances(estate.id, propertyUseType);
  const [proLaboreCommission, setProLaboreCommission] = useState<number | undefined>(
    undefined
  );
  const [assistanceId, setAssistanceId] = useState<number | null>(null);
  const [proLaboreCommissionMin, setProLaboreCommissionMin] = useState<number>(0);
  const [proLaboreCommissionMax, setProLaboreCommissionMax] = useState<number>(0);
  const [changedRentValue, setChangedRentValue] = useState(false);
  const [activeButtons, setActiveButtons] = useState(true);
  const [userCanSave, setUserCanSave] = useState<boolean>(false);

  const [address, setAddress] = useState(initialAddress);
  const [tenant, setTenant] = useState(initialTenant);
  const [insured, setInsured] = useState(initialInsured);

  useEffect(() => {
    if (initialAddress) {
      setAddress(initialAddress);
    }
  }, [initialAddress]);

  const [quotation, setQuotation] = useState({} as any);

  const [budgetId, setBudgetId] = useState('');
  const [isRenewal, setIsRenewal] = useState(false);
  const [installmentsOptions, setInstallmentsOptions] = useState<
    { key: number; text: string; value: number }[]
  >([]);

  const [selectedInstallment, setSelectedInstallment] = useState<number>(11);
  const [percentualPredio, setPercentualPredio] = useState<number>(90);
  const [percentualConteudo, setPercentualConteudo] = useState<number>(10);

  const [targetValue, setTargetValue] = useState<number>(0);
  const [isConfiguracoesCoberturaExpanded, setIsConfiguracoesCoberturaExpanded] =
    useState<boolean>(false);
  const [openAssistanceTableModal, setOpenAssistanceTableModal] =
    useState<boolean>(false);
  const [typeCoverage, setTypeCoverage] = useState<string>('PREDIO_CONTEUDO');

  // Lender Insurance
  const lenderInsuranceEnabled = useSelector(
    (state: iRootState) => state.availableBrokersAndEstates.lenderInsuranceEnabled ?? false
  );
  const lenderInsuranceRate = useSelector(
    (state: iRootState) => state.availableBrokersAndEstates.lenderInsuranceRate
  );
  const [includeLenderInsurance, setIncludeLenderInsurance] = useState<boolean>(false);

  const fireCoverageIndex =
    editableCoverages?.findIndex(coverage => isRequiredFireCoverage(coverage)) ?? -1;
  const fireCoverage =
    fireCoverageIndex >= 0 ? editableCoverages?.[fireCoverageIndex] : undefined;
  const additionalCoverages =
    editableCoverages
      ?.map((coverage, coverageIndex) => ({ coverage, coverageIndex }))
      .filter(({ coverageIndex }) => coverageIndex !== fireCoverageIndex) ?? [];

  useEffect(() => {
    const has = hasPermission(ActionEnum.save, FeatureEnum.fireinsurances);
    setUserCanSave(has && !isBackofficeTest);
  }, [location.pathname, isBackofficeTest]);

  useEffect(() => {
    async function fetchQuote(budgetId: string) {
      const data = await get(`/fire-insurance-quotations/segimob/budget/${budgetId}`);

      setPropertyType(data.propertyType);
      setPropertyUseType(+data.occupation);
      setMaskedRentValue(formatAmount(Number(data.rentValue).toFixed(2)));
      setMonths(data.vigency);
      if (!isRenewal) {
        setStartDate(new Date(data.startDate));
      }
      setAssistanceId(data.assistanceId);
      setProLaboreCommission(data.proLaborePercentageValue);
      setAddress(data.address);
      setTenant(data.tenant);
      setInsured(data.insured);
      setQuotation({
        rentValue: Number(data.rentValue).toFixed(2),
        vigency: data.vigency,
        startDate: new Date(data.startDate)
      });
      if (data.percentualPredio) {
        setPercentualPredio(data.percentualPredio);
      }
      if (data.percentualConteudo) {
        setPercentualConteudo(data.percentualConteudo);
      }
      setBudgetId(budgetId);
      if (data.buildingPercent) {
        setPercentualPredio(data.buildingPercent);
      }
      if (data.contentPercent) {
        setPercentualConteudo(data.contentPercent);
      }
      const normalize = (v: any, maxDecimals = 8) => {
        if (v == null || v === '') return v;
        const n = Number(v);
        if (Number.isNaN(n)) return v;
        return Number(n.toFixed(maxDecimals).replace(/\.?0+$/, ''));
      };
      if (data.coverages) {
        const fcoverages = data.coverages.map((coverage: any) => {
          return {
            ...coverage,
            flagSelecionada: coverage.enabled,
            nomeCobertura: coverage.coverageName,
            valorImportanciaSegurada: normalize(coverage.value),
            valorImportanciaSeguradaMasked: numberToMoneyString(
              normalize(coverage.value)
            ),
            valorLimiteMaximoImportanciaSegurada: normalize(coverage.maxValue),
            valorLimiteMinimoImportanciaSegurada: normalize(coverage.minValue),
            valorPremioTotal: normalize(coverage.totalPremium),
            id: coverage.referenceCode
          };
        });
        const fcoveragesSorted = sortRequiredFireCoverageFirst(
          fcoverages.sort((a: any, b: any) => Number(a.id) - Number(b.id))
        );
        setEditableCoverages(fcoveragesSorted);
      }
      if (data.buildingPercent && data.contentPercent) {
        if (data.buildingPercent === '0.00' && data.contentPercent === '100.00') {
          setTypeCoverage('CONTEUDO');
        } else if (data.buildingPercent === '100.00' && data.contentPercent === '0.00') {
          setTypeCoverage('PREDIO');
        } else {
          setTypeCoverage('PREDIO_CONTEUDO');
        }
      }
      setSelectedInstallment(data.installments || 11);
    }

    const bid = location.pathname.replace('/admin/fire-insurance-budget/', '');
    if (isUuid(bid)) {
      fetchQuote(bid);
    }
    const isRenewal = location.search.includes('RENEWAL');
    setIsRenewal(isRenewal);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (defaultAssistanceId !== null && assistanceId === null) {
      setAssistanceId(defaultAssistanceId);
    }
  }, [defaultAssistanceId]);

  useEffect(() => {
    if (initialRentValue) {
      setMaskedRentValue(formatAmount(Number(initialRentValue).toFixed(2)));
    }

    if (initialStartDate) {
      const parsedDate = new Date(initialStartDate);
      setStartDate(parsedDate);
    }
  }, [initialRentValue, initialStartDate]);

  const monthsOptions = (): { key: number; text: string; value: number }[] => {
    return Array.from(Array(12).keys()).map(month => {
      return {
        key: month + 1,
        text: `${month + 1} ${month >= 1 ? 'Meses' : 'Mês'}`,
        value: month + 1
      };
    });
  };

  async function handleQuote() {
    const payload = {
      estateId: estate.id,
      startDate,
      assistanceId,
      changedRentValue,
      vigency: months,
      insuranceTypeId: propertyType,
      occupationId: propertyUseType,
      rentValue: maskedRentValue ? moneyStringToNumber(maskedRentValue) : 0,
      proLaborePercentageValue: proLaboreCommission,
      percentualPredio,
      percentualConteudo,
      targetValue,
      coverages: quote?.coverages
        ? quote?.coverages
        : [
          {
            id: 0,
            valorImportanciaSegurada: 0,
            flagSelecionada: true,
            valorPremioTotal: 0
          }
        ]
    };

    setLoadingQuote(true);
    try {
      const response: SegimobQuotedProps = await post(
        '/fire-insurance-quotations/segimob/quote',
        payload
      );

      if (response.coverages && response.installments) {
        const fcoverages = response.coverages.map(coverage => {
          if (isRequiredFireCoverage(coverage)) {
            return {
              ...coverage,
              valorImportanciaSeguradaMasked: numberToMoneyString(
                coverage.valorImportanciaSegurada
              )
            };
          }
          return coverage;
        });

        response.coverages = sortRequiredFireCoverageFirst(fcoverages);

        const installments = response.installments.map((installment, i) => ({
          key: installment.numeroParcela,
          text: `${installment.numeroParcela} x ${numberToMoneyString(
            installment.valorParcela
          )}`,
          value: installment.numeroParcela
        }));

        setInstallmentsOptions(installments);
        setQuote(response);
        setChangedRentValue(false);
        setProLaboreCommission(+response.proLaborePercentageValue);
        setProLaboreCommissionMin(+response.proLaboreMinPercentageValue);
        setProLaboreCommissionMax(+response.proLaboreMaxPercentageValue);
        if (!editableCoverages) {
          setEditableCoverages(response.coverages);
        }
        setAssistanceId(response.assistanceId);
        setStep(2);
      } else {
        dangerNotification('Erro', 'Não foi possível realizar a cotação!');
      }
    } catch (err) {
      console.log(err);
      dangerNotification('Erro', 'Não foi possível realizar a cotação!');
    } finally {
      setLoadingQuote(false);
      setQuoteControl(false);
    }
  }

  useEffect(() => {
    if (
      startDate &&
      months &&
      propertyUseType &&
      assistanceId &&
      proLaboreCommission &&
      maskedRentValue
    ) {
      setActiveButtons(false);
    } else {
      setActiveButtons(true);
    }
  }, [
    assistanceId,
    maskedRentValue,
    months,
    proLaboreCommission,
    propertyUseType,
    startDate
  ]);

  useEffect(() => {
    setTargetValue(0);
    quoteControl && handleQuote();
  }, [quoteControl]);

  const handleSaveQuotation = (event: SyntheticEvent, hiringFlag: boolean) => {
    event.preventDefault();
    if (isBackofficeTest && !hiringFlag) {
      warningNotification(
        'Modo de teste',
        'Este perfil pode simular o orçamento, mas não pode salvá-lo ou contratá-lo.'
      );
      return;
    }
    if (!userCanSave && !isBackofficeTest) {
      warningNotification(
        'Permissão insuficiente',
        'Este perfil não possui permissão para salvar ou contratar este orçamento.'
      );
      return;
    }
    setIsHiring(hiringFlag);
    setIsSaveQuotationModalOpen(true);
  };

  const handleCloseSaveQuotationModal = (event: SyntheticEvent) => {
    event.preventDefault();
    setIsSaveQuotationModalOpen(false);
  };

  const handleSubmitQuotationModal = async (data: SaveQuotationModalFormProps) => {
    if (isBackofficeTest) {
      warningNotification(
        'Modo de teste',
        'Este perfil pode simular o orçamento, mas não pode salvá-lo ou contratá-lo.'
      );
      return;
    }

    try {
      setIsSaveQuotationModalLoading(true);

      const payload = {
        renewalFireInsuranceId: isRenewal ? budgetId : undefined,
        budgetId: isRenewal ? undefined : budgetId,
        estateId: estate.id,
        insurerId,
        isIframe,
        propertyType,
        property_public_id: data.propertyPublicId,
        payload: {
          codigoExternoImovel: '',
          quantidadeParcelasSelecionada: selectedInstallment,
          valorParcela: quote?.installments.find(
            installment => installment.numeroParcela === selectedInstallment
          )?.valorParcela,
          calculo: {
            idSeguradora: 'TOKIO_MARINE',
            idCorretora: '221',
            idImobiliaria: estate.segimobCode,
            idOcupacao: propertyUseType,
            idAssistencia: assistanceId,
            valorAluguel: moneyStringToNumber(maskedRentValue || ''),
            dataInicialVigencia: startDate,
            mesesVigencia: months,
            valorPremioTotal: quote?.totalValue,
            valorPercentualProlabore: proLaboreCommission,
            valorPercentualComissaoCorretora: quote?.brokerCommissionPercentageValue,
            flagAluguelAlterado: true,
            valorPercentualPredio: percentualPredio,
            valorPercentualConteudo: percentualConteudo,
            coberturas: quote?.coverages
          },
          endereco: {
            cep: data.propertyAddress.zipCode,
            logradouro: data.propertyAddress.street,
            numero: data.propertyAddress.number,
            complemento: sanitizeAddressComplement(
              data.propertyAddress.complement || ''
            ),
            bairro: data.propertyAddress.neighborhood,
            uf: data.propertyAddress.state,
            cidade: data.propertyAddress.city
          },
          inquilino: {
            nome: data.tenantIdentification.name,
            cpfCnpj: data.tenantIdentification.document?.replace(/\D/g, ''),
            email: data.tenantIdentification.email,
            dataNascimento: data.tenantIdentification.birthdate,
            numeroTelefone: data.tenantIdentification.phoneNumber?.replace(/\D/g, '')
          },
          proprietario: {
            nome: data.ownerIdentification.name,
            cpfCnpj: data.ownerIdentification.document?.replace(/\D/g, ''),
            email: data.ownerIdentification.email
          }
        }
      };

      const response = await post('/fire-insurance-quotations/segimob/budget', payload);

      if (response) {
        if (onBudgetCreated) {
          onBudgetCreated(response.fireInsuranceId);
        }
        if (isHiring) {
          try {
            const data = await post('/fire-insurance-proposals/hire/segimob', {
              fireInsuranceAnalysisId: response.id
            });

            if (data.success) {
              PushRoute(history, props, 'fire-insurance-policies');
              return;
            }
          } catch (err) {
            console.error('Erro ao realizar contratação! ', err);
            dangerNotification(
              'Solicitação de Contratação',
              'Erro ao realizar contratação!'
            );
          }
        }
        setIsSaveQuotationModalOpen(false);
        PushRoute(history, props, 'fire-insurance-budgets');
      }
    } catch (error) {
      console.error('Erro Segimob! ', error);
      dangerNotification(
        'Erro',
        'Não foi possível realizar o orçamento! ' + (error as any)?.message || '',
        6000
      );
      setIsSaveQuotationModalLoading(false);
    } finally {
      setIsSaveQuotationModalLoading(false);
    }
  };

  const handleStartDate = (date: Date) => {
    setStartDate(date);

    if (step === 2) {
      setQuoteControl(true);
    }
  };

  const handleMonths = (value: number) => {
    setMonths(value);

    if (step === 2) {
      setQuoteControl(true);
    }
  };

  const handleAssistance = (value: number) => {
    setAssistanceId(value as number);

    if (step === 2) {
      setQuoteControl(true);
    }
  };

  const handleProLaboreCommission = (hasChange: boolean) => {
    if (step === 2) {
      if (hasChange) {
        setQuoteControl(true);
      }
    }
  };

  const handleRentValue = (hasChange: boolean) => {
    if (step === 2) {
      if (hasChange) {
        setQuoteControl(true);
      }
      setChangedRentValue(true);
    }
  };

  return (
    <S.Container>
      <S.FullWidthGrid centered>
        <S.CenteredColumn computer={step === 1 ? 8 : 8} tablet={16} mobile={16}>
          <S.FullWidthGrid centered>
            <Grid.Row>
              <Grid.Column width={16}>
                <S.Subtitle> Dados para cotação </S.Subtitle>
              </Grid.Column>
              <Grid.Column width={16}>
                <S.Hr />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <S.ColumnWithMargin computer={8} tablet={8} mobile={16}>
                <FieldLabel>INÍCIO DE VIGÊNCIA</FieldLabel>
                <S.DatePickerWrapper>
                  <CustomDatePicker
                    placeholderText={'Data Inicial'}
                    selected={startDate ?? undefined}
                    minDate={minRange}
                    maxDate={maxRange}
                    onChange={handleStartDate}
                    customInput={
                      <AppMaskedInput
                        mask={[
                          /[0-9]/,
                          /[0-9]/,
                          '/',
                          /[0-9]/,
                          /[0-9]/,
                          '/',
                          /[0-9]/,
                          /[0-9]/,
                          /[0-9]/,
                          /[0-9]/
                        ]}
                      />
                    }
                    dateFormat={'dd/MM/yyyy'}
                  />
                </S.DatePickerWrapper>
              </S.ColumnWithMargin>

              <S.ColumnWithMargin computer={8} tablet={8} mobile={16}>
                <FieldLabel paddingBottom>MESES DE VIGÊNCIA</FieldLabel>
                <Dropdown
                  fluid
                  search
                  selection
                  placeholder={'Número de Meses'}
                  options={monthsOptions()}
                  onChange={(e, { value }) => handleMonths(value as number)}
                  value={months ?? undefined}
                />
              </S.ColumnWithMargin>
            </Grid.Row>
            <Grid.Row>
              <S.ColumnWithMargin
                computer={step === 1 ? 8 : 16}
                tablet={step === 1 ? 8 : 16}
                mobile={16}
              >
                <FieldLabel paddingBottom>TIPO DE IMÓVEL</FieldLabel>
                <Dropdown
                  fluid
                  search
                  selection
                  options={occupations}
                  placeholder={'Tipo de Ocupação'}
                  onChange={(e, { value }) => {
                    setPropertyUseType(value as number);
                    setAssistanceId(null);
                  }}
                  value={propertyUseType}
                  loading={loadingOccupations}
                />
              </S.ColumnWithMargin>

              {step === 1 && (
                <S.ColumnWithMargin computer={8} tablet={8} mobile={16}>
                  <FieldLabel paddingBottom>VALOR DO ALUGUEL</FieldLabel>
                  <Input
                    fluid
                    placeholder={'Valor do Aluguel'}
                    icon={'dollar'}
                    iconPosition={'left'}
                    onChange={(e, data) => {
                      setRentValueForComparison(maskedRentValue);
                      const value = formatAmount(data.value);
                      setMaskedRentValue(value);
                    }}
                    value={maskedRentValue}
                    onBlur={handleRentValue}
                  />
                </S.ColumnWithMargin>
              )}
            </Grid.Row>

            {step === 2 && (
              <Grid.Row>
                <S.ColumnWithMargin
                  computer={canEditPl ? 8 : 16}
                  tablet={canEditPl ? 8 : 16}
                  mobile={16}
                >
                  <FieldLabel paddingBottom>ASSISTÊNCIA*</FieldLabel>
                  <Dropdown
                    fluid
                    search
                    selection
                    placeholder={'Assistência'}
                    options={assistances}
                    onChange={(e, { value }) => handleAssistance(value as number)}
                    value={assistanceId ? +assistanceId : undefined}
                    loading={loadingAssistances}
                  />
                </S.ColumnWithMargin>
                {canEditPl && (
                <S.ColumnWithMargin computer={8} tablet={8} mobile={16}>
                  <FieldLabel paddingBottom>PL (%)</FieldLabel>
                  <Input
                    type="number"
                    placeholder={'Pro-labore da imobiliária'}
                    value={proLaboreCommission}
                    onFocus={() => {
                      setProLaboreCommissionComparison(proLaboreCommission);
                    }}
                    onChange={(e, { value }) => {
                      if (value) {
                        if (+value < proLaboreCommissionMin) {
                          warningNotification(
                            'Atenção',
                            `O valor do PL deve ser entre ${proLaboreCommissionMin}% e ${proLaboreCommissionMax}%`
                          );
                          setProLaboreCommission(proLaboreCommissionMin);
                          return;
                        }
                        if (+value > proLaboreCommissionMax) {
                          warningNotification(
                            'Atenção',
                            `O valor do PL deve ser entre ${proLaboreCommissionMin}% e ${proLaboreCommissionMax}%`
                          );
                          setProLaboreCommission(proLaboreCommissionMax);
                          return;
                        }

                        setProLaboreCommission(+value);
                      }
                    }}
                    min={proLaboreCommissionMin}
                    max={proLaboreCommissionMax}
                    onBlur={(e: any) => {
                      const hasChange =
                        Number(proLaboreCommissionComparison) !== Number(e.target.value);
                      handleProLaboreCommission(hasChange);
                    }}
                    fluid
                  />
                </S.ColumnWithMargin>
                )}
              </Grid.Row>
            )}

            {step === 2 && (
              <Grid.Row>
                <Grid.Column width={8}>
                  <FieldLabel paddingBottom>VALOR DO ALUGUEL</FieldLabel>
                  <Input
                    fluid
                    placeholder={'Valor do Aluguel'}
                    icon={'dollar'}
                    iconPosition={'left'}
                    onFocus={() => {
                      setRentValueForComparison(maskedRentValue);
                    }}
                    onChange={(e, data) => {
                      setRentValueForComparison(maskedRentValue);
                      const value = formatAmount(data.value);
                      setMaskedRentValue(value);
                    }}
                    value={maskedRentValue}
                    onBlur={(e: any) => {
                      const hasChange = rentValueForComparison !== e.target.value;
                      handleRentValue(hasChange);
                    }}
                  />
                </Grid.Column>

                <Grid.Column width={8}>
                  <FieldLabel paddingBottom>PARCELAS</FieldLabel>
                  <Dropdown
                    fluid
                    selection
                    placeholder={'Número de Parcelas'}
                    value={selectedInstallment}
                    options={installmentsOptions}
                    onChange={(e, { value }) => {
                      setSelectedInstallment(value as number);
                    }}
                  />
                </Grid.Column>
              </Grid.Row>
            )}

            {canViewAdvancedSettings && (
            <div
              style={{
                margin: '20px 0',
                alignItems: 'center',
                width: '100%',
                display: 'flex'
              }}
            >
              <div style={{ fontSize: 14, color: '#666', whiteSpace: 'nowrap' }}>
                *Opcional
              </div>
              <div
                style={{
                  flexGrow: 1,
                  height: 1,
                  backgroundColor: '#dcdcdc',
                  margin: '0 10px'
                }}
              ></div>
              <div
                style={{
                  color: '#5f2dab',
                  fontSize: 14,
                  fontWeight: 800,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4
                }}
                onClick={() =>
                  setIsConfiguracoesCoberturaExpanded(!isConfiguracoesCoberturaExpanded)
                }
              >
                Configurações avançadas
                <i
                  className="icone"
                  style={{
                    border: '1px solid #5f2dab',
                    borderWidth: '0 2px 2px 0',
                    display: 'inline-block',
                    padding: 4,
                    transform: isConfiguracoesCoberturaExpanded
                      ? 'rotate(-135deg)'
                      : 'rotate(45deg)',
                    transition: 'transform 0.3s ease'
                  }}
                ></i>
              </div>
            </div>
            )}

            {canViewAdvancedSettings && isConfiguracoesCoberturaExpanded && (
              <>
                <Grid.Row>
                  <Grid.Column width={16}>
                    <FieldLabel paddingBottom>Tipo de cobertura</FieldLabel>
                    <Dropdown
                      fluid
                      search
                      selection
                      placeholder={'Tipo de cobertura'}
                      options={[
                        {
                          key: 'PREDIO',
                          text: 'Prédio',
                          value: 'PREDIO'
                        },
                        {
                          key: 'CONTEUDO',
                          text: 'Conteúdo',
                          value: 'CONTEUDO'
                        },
                        {
                          key: 'PREDIO_CONTEUDO',
                          text: 'Prédio + Conteúdo',
                          value: 'PREDIO_CONTEUDO'
                        }
                      ]}
                      defaultValue={typeCoverage}
                      onChange={(e, { value }) => {
                        setTypeCoverage(value as string);
                        if (value === 'PREDIO') {
                          setPercentualPredio(100);
                          setPercentualConteudo(0);
                        } else if (value === 'CONTEUDO') {
                          setPercentualPredio(0);
                          setPercentualConteudo(100);
                        }

                        if (value === 'PREDIO_CONTEUDO') {
                          setPercentualPredio(90);
                          setPercentualConteudo(10);
                        }
                        if (step === 2) {
                          setQuoteControl(true);
                        }
                      }}
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <S.ColumnWithMargin computer={8} tablet={8} mobile={16}>
                    <FieldLabel paddingBottom>Prédio (%)</FieldLabel>
                    <Input
                      type="number"
                      fluid
                      placeholder={'Percentual Prédio'}
                      value={percentualPredio}
                      min={0}
                      max={100}
                      onChange={(e, { value }) => {
                        const numValue = Number(value);
                        if (numValue >= 0 && numValue <= 100) {
                          setPercentualPredio(numValue);
                          setPercentualConteudo(100 - numValue);
                        }

                        if (step === 2) {
                          setQuoteControl(true);
                        }
                      }}
                    />
                  </S.ColumnWithMargin>

                  <S.ColumnWithMargin computer={8} tablet={8} mobile={16}>
                    <FieldLabel paddingBottom>Conteúdo (%)</FieldLabel>
                    <Input
                      type="number"
                      fluid
                      placeholder={'Percentual Conteúdo'}
                      value={percentualConteudo}
                      min={0}
                      max={100}
                      onChange={(e, { value }) => {
                        const numValue = Number(value);
                        if (numValue >= 0 && numValue <= 100) {
                          setPercentualConteudo(numValue);
                          setPercentualPredio(100 - numValue);
                        }
                      }}
                      onBlur={() => {
                        if (step === 2) {
                          setQuoteControl(true);
                        }
                      }}
                    />
                  </S.ColumnWithMargin>
                </Grid.Row>
              </>
            )}
          </S.FullWidthGrid>
        </S.CenteredColumn>

        <Grid.Column computer={step === 1 ? 8 : 8} tablet={16} mobile={16}>
          {step === 1 ? (
            <S.PreQuoteWrapper>
              <S.PreQuoteHeader>
                <span>
                  <Icon name="shield" /> Validação da Cotação
                </span>
              </S.PreQuoteHeader>
              <Grid style={{ padding: 10, height: 'calc(100% - 60px)' }}>
                <Grid.Row>
                  <S.RequirementsWrapper>
                    <div
                      style={{ cursor: startDate ? 'default' : 'help', marginBottom: 5 }}
                    >
                      <Icon
                        name={startDate ? 'check' : 'times'}
                        color={startDate ? 'green' : 'red'}
                      />
                      <strong style={{ color: startDate ? 'green' : 'red' }}>
                        Início de Vigência
                      </strong>
                    </div>

                    <div style={{ cursor: months ? 'default' : 'help', marginBottom: 5 }}>
                      <Icon
                        name={months ? 'check' : 'times'}
                        color={months ? 'green' : 'red'}
                      />
                      <strong style={{ color: months ? 'green' : 'red' }}>
                        Meses de vigência
                      </strong>
                    </div>

                    <div
                      style={{
                        cursor: propertyUseType ? 'default' : 'help',
                        marginBottom: 5
                      }}
                    >
                      <Icon
                        name={propertyUseType ? 'check' : 'times'}
                        color={propertyUseType ? 'green' : 'red'}
                      />
                      <strong style={{ color: propertyUseType ? 'green' : 'red' }}>
                        Tipo de ocupação
                      </strong>
                    </div>

                    <div
                      style={{
                        cursor:
                          maskedRentValue && moneyStringToNumber(maskedRentValue) > 0
                            ? 'default'
                            : 'help',
                        marginBottom: 5
                      }}
                    >
                      <Icon
                        name={
                          maskedRentValue && moneyStringToNumber(maskedRentValue) > 0
                            ? 'check'
                            : 'times'
                        }
                        color={
                          maskedRentValue && moneyStringToNumber(maskedRentValue) > 0
                            ? 'green'
                            : 'red'
                        }
                      />
                      <strong
                        style={{
                          color:
                            maskedRentValue && moneyStringToNumber(maskedRentValue) > 0
                              ? 'green'
                              : 'red'
                        }}
                      >
                        Valor do aluguel
                      </strong>
                    </div>
                  </S.RequirementsWrapper>
                </Grid.Row>
                <Grid.Row>
                  <S.ColumnWithMargin>
                    <Button
                      fluid
                      positive
                      disabled={
                        !startDate ||
                        !months ||
                        !propertyUseType ||
                        !maskedRentValue ||
                        moneyStringToNumber(maskedRentValue) <= 0 ||
                        loadingQuote
                      }
                      loading={loadingQuote}
                      onClick={() => handleQuote()}
                      content={
                        budgetId && !isRenewal ? 'Atualizar cotação' : 'Solicitar cotação'
                      }
                    />
                  </S.ColumnWithMargin>
                </Grid.Row>
              </Grid>
            </S.PreQuoteWrapper>
          ) : (
            <S.PreQuoteWrapper>
              <S.PreQuoteHeader>
                <span className="left">
                  <Icon name="shield" />
                  Orçamento de Seguro
                </span>
              </S.PreQuoteHeader>
              <Grid style={{ height: 'calc(100% - 60px)', margin: 0, display: 'flex' }}>
                <S.SecctionName> Coberturas disponíveis </S.SecctionName>
                <div style={{ paddingTop: '10px', marginLeft: '-20px' }}>
                  <HiInformationCircle
                    onClick={() => setOpenAssistanceTableModal(true)}
                    style={{ cursor: 'pointer' }}
                    size="20px"
                    color={theme.main.color}
                  />
                </div>
                {quote?.coverages && editableCoverages && (
                  <>
                    {fireCoverage && (
                      <S.CoveragesRow>
                        <div>
                          <span className="insurance-name">
                            <Icon name="shield" className="low-opacity" /> Incêndio &nbsp;
                            <span className="required">(Obrigatório)</span>
                          </span>
                        </div>
                        <div>
                          {editableCoverageCode === -1 ? (
                            <div className="editable-coverages">
                              <Input
                                className="editable-coverages-input"
                                fluid
                                iconPosition={'left'}
                                value={
                                  fireCoverage.valorImportanciaSeguradaMasked ??
                                  numberToMoneyString(
                                    +fireCoverage.valorImportanciaSegurada
                                  )
                                }
                                onChange={e => {
                                  setEditableCoverages(
                                    editableCoverages.map((coverage, i) => {
                                      if (i === fireCoverageIndex) {
                                        return {
                                          ...coverage,
                                          valorImportanciaSeguradaMasked: formatAmount(
                                            e.target.value
                                          )
                                        };
                                      }
                                      return coverage;
                                    })
                                  );
                                }}
                              />
                              <Icon
                                className="icon"
                                name="check"
                                color="green"
                                onClick={() => {
                                  const numberValue = unmaskMoney(
                                    fireCoverage.valorImportanciaSeguradaMasked
                                  );

                                  if (
                                    numberValue >
                                    fireCoverage
                                      .valorLimiteMaximoImportanciaSegurada!
                                  ) {
                                    dangerNotification(
                                      'Atenção',
                                      `O valor máximo para essa cobertura é de R$ ${numberToMoneyString(
                                        fireCoverage
                                          .valorLimiteMaximoImportanciaSegurada!
                                      )}`
                                    );
                                    return;
                                  }

                                  if (
                                    numberValue <
                                    fireCoverage
                                      .valorLimiteMinimoImportanciaSegurada!
                                  ) {
                                    dangerNotification(
                                      'Atenção',
                                      `O valor mínimo para essa cobertura é de R$ ${numberToMoneyString(
                                        fireCoverage
                                          .valorLimiteMinimoImportanciaSegurada!
                                      )}`
                                    );
                                    return;
                                  }

                                  setEditableCoverageCode(-2);
                                  const valorImportanciaSegurada = moneyStringToNumber(
                                    fireCoverage.valorImportanciaSeguradaMasked
                                  );
                                  editableCoverages[
                                    fireCoverageIndex
                                  ].valorImportanciaSegurada = valorImportanciaSegurada;
                                  quote.coverages[fireCoverageIndex].valorImportanciaSegurada =
                                    valorImportanciaSegurada;
                                  setQuoteControl(true);
                                }}
                              />
                              <Icon
                                className="icon"
                                name="close"
                                color="red"
                                onClick={() => {
                                  setEditableCoverageCode(-2);
                                  setEditableCoverages(quote.coverages);
                                }}
                              />
                            </div>
                          ) : (
                            <span className="insurance-value">
                              {fireCoverage?.valorImportanciaSegurada
                                ? numberToMoneyString(
                                  fireCoverage?.valorImportanciaSegurada
                                )
                                : 'R$ x.xxx,xx'}
                              <Icon
                                name="pencil"
                                color="black"
                                className="low-opacity pencil-icon"
                                onClick={() => {
                                  setEditableCoverageCode(-1);

                                  setEditableCoverages(
                                    editableCoverages.map((coverage, i) => {
                                      if (i === fireCoverageIndex) {
                                        return {
                                          ...coverage,
                                          valorImportanciaSeguradaMasked:
                                            coverage.valorImportanciaSeguradaMasked ??
                                            numberToMoneyString(
                                              +coverage.valorImportanciaSegurada
                                            )
                                        };
                                      }
                                      return coverage;
                                    })
                                  );
                                }}
                              />
                            </span>
                          )}
                        </div>
                      </S.CoveragesRow>
                    )}
                    <S.CoveragesRow>
                      <div
                        className="low-opacity"
                        onClick={() => {
                          setEditableCoverages(
                            editableCoverages.map((coverage, i) => {
                              if (i !== fireCoverageIndex) {
                                return {
                                  ...coverage,
                                  flagSelecionada: !allChecked
                                };
                              }
                              return coverage;
                            })
                          );
                          setAllChecked(!allChecked);
                        }}
                      >
                        <span style={{ cursor: 'pointer' }}>
                          <input type="checkbox" checked={allChecked} />
                          Habilitar/Desabilitar todas as coberturas adicionais
                        </span>
                      </div>
                    </S.CoveragesRow>

                    {additionalCoverages.map(({ coverage, coverageIndex }, index) => (
                      <S.CoveragesRow key={coverageIndex} offwhite={index % 2 === 0}>
                        <div>
                          {coverage.nomeCobertura && (
                            <span
                              className={
                                editableCoverageCode === coverageIndex
                                  ? 'slice-insurance-name'
                                  : 'full-insurance-name'
                              }
                            >
                              <input
                                type="checkbox"
                                checked={coverage.flagSelecionada}
                                onClick={() => {
                                  const cvgs = editableCoverages.map((coverage, i) => {
                                    if (i === coverageIndex) {
                                      return {
                                        ...coverage,
                                        flagSelecionada: !coverage.flagSelecionada,
                                        valorImportanciaSegurada:
                                          quote.coverages[i].valorImportanciaSegurada
                                      };
                                    }
                                    return coverage;
                                  });
                                  setQuote({
                                    ...quote,
                                    coverages: cvgs
                                  });
                                  setEditableCoverageCode(-2);
                                  setEditableCoverages(cvgs);
                                  setQuoteControl(true);
                                }}
                              />
                              {coverage.nomeCobertura[0] +
                                coverage.nomeCobertura?.toLowerCase().slice(1)}
                            </span>
                          )}
                        </div>
                        <div>
                          {editableCoverageCode !== coverageIndex ? (
                            <>
                              <span className="insurance-value">
                                {numberToMoneyString(coverage.valorImportanciaSegurada)}
                              </span>
                              {coverage.flagSelecionada && (
                                <Icon
                                  name="pencil"
                                  className="low-opacity  pencil-icon"
                                  onClick={() => setEditableCoverageCode(coverageIndex)}
                                />
                              )}
                            </>
                          ) : (
                            <div className="editable-coverages">
                              <Input
                                className="editable-coverages-input"
                                fluid
                                iconPosition={'left'}
                                value={
                                  coverage.valorImportanciaSeguradaMasked ??
                                  numberToMoneyString(+coverage.valorImportanciaSegurada)
                                }
                                onChange={e => {
                                  setEditableCoverages(
                                    editableCoverages.map((coverage, i) => {
                                      if (i === coverageIndex) {
                                        return {
                                          ...coverage,
                                          valorImportanciaSeguradaMasked: formatAmount(
                                            e.target.value
                                          )
                                        };
                                      }
                                      return coverage;
                                    })
                                  );
                                }}
                              />
                              <Icon
                                className="icon"
                                name="check"
                                color="green"
                                onClick={() => {
                                  const numberValue = unmaskMoney(
                                    coverage.valorImportanciaSeguradaMasked
                                  );

                                  if (
                                    numberValue >
                                    coverage.valorLimiteMaximoImportanciaSegurada!
                                  ) {
                                    dangerNotification(
                                      'Atenção',
                                      `O valor máximo para essa cobertura é de R$ ${numberToMoneyString(
                                        coverage.valorLimiteMaximoImportanciaSegurada!
                                      )}`
                                    );
                                    return;
                                  }
                                  if (
                                    numberValue <
                                    coverage.valorLimiteMinimoImportanciaSegurada!
                                  ) {
                                    dangerNotification(
                                      'Atenção',
                                      `O valor mínimo para essa cobertura é de R$ ${numberToMoneyString(
                                        coverage.valorLimiteMinimoImportanciaSegurada!
                                      )}`
                                    );
                                    return;
                                  }
                                  setEditableCoverageCode(-2);
                                  const updatedCoverages = editableCoverages.map(
                                    (cv, i) => {
                                      if (i === coverageIndex) {
                                        return {
                                          ...cv,
                                          valorImportanciaSegurada: numberValue,
                                          valorImportanciaSeguradaMasked: '' // Reseta o valor mascarado
                                        };
                                      }
                                      return cv;
                                    }
                                  );

                                  setEditableCoverages(updatedCoverages);
                                  quote.coverages[coverageIndex].valorImportanciaSegurada =
                                    numberValue;
                                  setQuoteControl(true);
                                }}
                              />
                              <Icon
                                className="icon"
                                name="close"
                                color="red"
                                onClick={() => {
                                  setEditableCoverageCode(-2);
                                  setEditableCoverages(quote.coverages);
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </S.CoveragesRow>
                    ))}

                    <S.Summarysection>
                      <div className="budget">
                        <span className="title">Seu orçamento</span>
                        <div>
                          <h1>
                            <span>
                              {
                                quote.installments.find(
                                  installment =>
                                    installment.numeroParcela === selectedInstallment
                                )?.numeroParcela
                              }
                              x&nbsp;
                            </span>
                            {numberToMoneyString(
                              + Number(quote.installments.find(
                                installment =>
                                  installment.numeroParcela === selectedInstallment
                              )?.valorParcela || 0).toFixed(2)
                            )}
                          </h1>
                          <span className="total">
                            Total: {numberToMoneyString(+ Number(quote.totalValue).toFixed(2))}
                          </span>
                        </div>
                        <S.ColumnWithMargin style={{ marginTop: 20 }}>
                          <FieldLabel paddingBottom>
                            Calcular por valor{' '}
                            <Popup
                              trigger={
                                <Icon
                                  name="info circle"
                                  style={{ color: theme.main.color, cursor: 'pointer' }}
                                />
                              }
                              content={
                                <div style={{ display: 'flex', width: '360px' }}>
                                  <ul style={{ marginLeft: 0, paddingLeft: '24px' }}>
                                    <li style={{ marginBottom: '16px' }}>
                                      <h4>Calculo por valor:</h4>
                                      <p>
                                        O sistema irá tentar chegar o mais próximo
                                        possível ao valor informado, reduzindo coberturas,
                                        assistencias e comissão! Se deseja controlar
                                        manualmente e ajustar os valores de cobertura e
                                        assistencia, deve deixar esse campo zerado!
                                      </p>
                                    </li>
                                  </ul>
                                </div>
                              }
                            />
                          </FieldLabel>
                          <Input
                            fluid
                            icon={'dollar'}
                            iconPosition={'left'}
                            onChange={(e, data) => {
                              setTargetValue(UnMaskCurrency(data.value));
                            }}
                            value={targetValue > 0 ? FormatCurrency(targetValue) : ''}
                            action={{
                              content: 'Aplicar',
                              color: '#59249B',
                              onClick: () => {
                                handleQuote();
                              }
                            }}
                          />
                        </S.ColumnWithMargin>
                      </div>

                      <div className="buttons">
                        {canContract && <Button
                          style={{ marginRight: 10 }}
                          primary
                          onClick={e => handleSaveQuotation(e, true)}
                          disabled={
                            (isBackofficeTest ? false : !userCanSave) ||
                            activeButtons ||
                            editableCoverageCode !== -2
                          }
                        >
                          <Icon name="clipboard check" />
                          Contratar
                        </Button>}
                        <Button
                          basic
                          primary
                          icon="save"
                          onClick={e => handleSaveQuotation(e, false)}
                          disabled={
                            !userCanSave || activeButtons || editableCoverageCode !== -2
                          }
                        >
                          <Icon name="download" />
                          {budgetId && !isRenewal
                            ? 'Atualizar cotação'
                            : 'Salvar Cotação'}
                        </Button>
                      </div>
                    </S.Summarysection>
                  </>
                )}
              </Grid>
            </S.PreQuoteWrapper>
          )}
        </Grid.Column>
      </S.FullWidthGrid>
      {quote && (
        <>
          <Dimmer active={isSaveQuotationModalLoading || loadingQuote} inverted page>
            <Loader size="big" />
          </Dimmer>

          <SaveQuotationModal
            isOpen={isSaveQuotationModalOpen}
            onClose={handleCloseSaveQuotationModal}
            quotation={{
              rentValue: maskedRentValue ?? '',
              vigency: months ?? '',
              startDate: startDate || new Date(),
              selectedInstallment: selectedInstallment,
              totalValue: quote?.totalValue
            }}
            occupation={propertyUseType}
            handleSubmit={handleSubmitQuotationModal}
            quote={quote}
            isHiring={isHiring}
            previousData={{ address, tenant, insured, quotation, budgetId }}
            estateId={estate.id}
          />
        </>
      )}

      {openAssistanceTableModal && (
        <AssistanceTableModal
          isOpen={openAssistanceTableModal}
          onClose={() => setOpenAssistanceTableModal(!openAssistanceTableModal)}
        />
      )}
    </S.Container>
  );
};

export default SegimobBudgetForm;
